import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { InvitePreview, UserSelf } from '@tt/shared';
import { Alert, Spinner } from '@/components/ui/misc';
import { api, isApiError } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { keys } from '@/lib/queries';
import { AuthLayout } from './auth-layout';
import { PasswordForm } from './password-form';

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const { token } = useSearch({ from: '/accept-invite' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const preview = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<InvitePreview>(`/auth/invite/${encodeURIComponent(token ?? '')}`),
    enabled: !!token,
    retry: false,
  });

  const submit = async (password: string) => {
    try {
      const { user } = await api<{ user: UserSelf }>('/auth/accept-invite', {
        method: 'POST',
        body: { token, password },
      });
      qc.setQueryData(keys.me, user);
      await navigate({ to: '/', replace: true });
    } catch (err) {
      throw new Error(
        isApiError(err) && err.status === 410 ? t('auth.tokenExpired') : describeError(err, t),
      );
    }
  };

  const invalid = !token || preview.isError;
  const title = preview.data
    ? t('auth.inviteTitle', { name: preview.data.firstName })
    : t('auth.acceptInvite');
  const description = preview.data
    ? t('auth.inviteHelp', { org: preview.data.organizationName, email: preview.data.email })
    : undefined;

  return (
    <AuthLayout title={title} description={description}>
      {invalid ? (
        <>
          <Alert variant="error">{t('auth.tokenExpired')}</Alert>
          <p className="mt-4 text-center text-sm">
            <Link
              to="/login"
              search={{}}
              className="text-primary underline-offset-4 hover:underline"
            >
              {t('auth.backToSignIn')}
            </Link>
          </p>
        </>
      ) : preview.isPending ? (
        <Spinner />
      ) : (
        <PasswordForm submitLabel={t('auth.acceptInvite')} onSubmit={submit} />
      )}
    </AuthLayout>
  );
}
